// COUNCIL mode's furniture on the Pantheon (Bothread's room, in marble): floating buttons (close,
// Conference read-aloud, transcript, sessions), the transcript sheet (who said what, ↳ replying to,
// Play / Pause per line), the composer (hands-free mic, @ tags, text), the seat menu (Nudge / Mute /
// talk 1:1 / remove / change), the picker for an empty podium, and SyQi's own seat box.
// All state comes from useCouncil(); SelfView decides where each piece sits on the floor.
import React, { useRef } from 'react';
import { KeyboardAvoidingView, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { Hand, History, Mic, Pause as PauseIcon, Play as PlayIcon, MicOff, Pause, Phone, Play, Plus, Repeat, Send, Trash2, UserMinus, Volume2, VolumeX, X } from 'lucide-react-native';
import { Butterfly } from '../v10/ButterflyIcon';
import { makeStyles, useV10 } from '../v10/theme';
import { Medallion } from '../v10/ui';
import type { CouncilLine } from './councilApi';
import { seatName, type Council } from './useCouncil';

const timeOf = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export type CouncilPanel = 'none' | 'sessions';

/** Close / Conference / transcript / sessions, stacked down the right edge (same look as the talk overlay). */
export function CouncilButtons({ c, top, panel, setPanel, onClose }: { c: Council; top: number; panel: CouncilPanel; setPanel: (p: CouncilPanel) => void; onClose: () => void }) {
  const t = useV10();
  const styles = useStyles();
  const btn = (y: number, icon: React.ReactNode, onPress: () => void, label: string, on = false) => (
    <Pressable onPress={onPress} style={[styles.float, { top: top + y }, on && styles.floatOn]} hitSlop={6} accessibilityLabel={label}>
      {icon}
    </Pressable>
  );
  return (
    <>
      {btn(0, <X size={18} color={t.ivory} />, onClose, 'Leave the council')}
      {btn(44, c.conference ? <Volume2 size={17} color={t.goldBright} /> : <VolumeX size={17} color={t.ivory} />, c.toggleConference, c.conference ? 'Conference on: replies are read aloud' : 'Conference off', c.conference)}
      {btn(88, <History size={17} color={panel === 'sessions' ? t.goldBright : t.ivory} />, () => {
        if (panel === 'sessions') return setPanel('none');
        void c.refreshSessions();
        setPanel('sessions');
      }, 'Past council sessions', panel === 'sessions')}
    </>
  );
}

/** The conversation, Bothread-style: avatar, name, time, Play / Pause, ↳ replying to, the line. */
export function CouncilTranscript({ c, style }: { c: Council; style: object }) {
  const t = useV10();
  const styles = useStyles();
  const ref = useRef<ScrollView | null>(null);
  const by = (id: string | null | undefined) => (id ? c.lines.find((x) => x.id === id) : undefined);
  return (
    <View style={[styles.sheet, style]}>
      <ScrollView ref={ref} onContentSizeChange={() => ref.current?.scrollToEnd({ animated: true })} contentContainerStyle={{ gap: 8, padding: 8 }}>
        {c.lines.map((l) =>
          l.kind === 'system' ? (
            <Text key={l.id} style={styles.sys}>
              {l.content}
            </Text>
          ) : (
            <View key={l.id} style={{ gap: 4 }}>
              <View style={styles.msgHead}>
                {l.speaker === 'user' ? (
                  <View style={styles.you}>
                    <Text style={styles.youText}>YOU</Text>
                  </View>
                ) : l.speaker === 'syqi' ? (
                  <View style={styles.you}>
                    <Butterfly size={16} color={t.goldBright} />
                  </View>
                ) : (
                  <Medallion id={l.speaker} size={28} />
                )}
                <Text style={styles.msgName}>{seatName(l.speaker)}</Text>
                <Text style={styles.msgTime}>{timeOf(l.created_at)}</Text>
                <View style={{ flex: 1 }} />
                {l.speaker !== 'user' && !l.id.startsWith('tmp-') ? (
                  <Pressable onPress={() => c.playLine(l)} style={styles.playBtn} accessibilityLabel={c.play.id === l.id && !c.play.paused ? `Pause ${seatName(l.speaker)}` : `Play ${seatName(l.speaker)}`}>
                    {c.play.id === l.id && !c.play.paused ? <Pause size={13} color={t.goldBright} /> : <Play size={13} color={t.goldBright} />}
                    <Text style={styles.playText}>{c.play.id === l.id ? (c.play.loading ? '…' : c.play.paused ? 'Play' : 'Pause') : 'Play'}</Text>
                  </Pressable>
                ) : null}
              </View>
              {l.reply_to ? (
                <Text style={styles.quote} numberOfLines={1}>
                  ↳ replying to {seatName(by(l.reply_to)?.speaker ?? '')}: {(by(l.reply_to)?.content ?? '').slice(0, 60)}
                </Text>
              ) : null}
              <Text style={styles.msgText}>{l.content}</Text>
            </View>
          ),
        )}
      </ScrollView>
    </View>
  );
}

/** Bothread's lobby: name a council and start it; your councils below with their status (active / paused / closed). */
export function CouncilSessions({ c, style, onPicked }: { c: Council; style: object; onPicked: () => void }) {
  const t = useV10();
  const styles = useStyles();
  const [title, setTitle] = React.useState('');
  return (
    <View style={[styles.sheet, style]}>
      <ScrollView contentContainerStyle={{ gap: 8, padding: 12 }} keyboardShouldPersistTaps="handled">
        <Text style={styles.lobbyKicker}>START A COUNCIL</Text>
        <View style={styles.inputRow}>
          <TextInput value={title} onChangeText={setTitle} placeholder="Council name" placeholderTextColor="#6b7280" style={styles.input} returnKeyType="go" onSubmitEditing={() => void c.start(title).then(onPicked)} />
          <Pressable onPress={() => void c.start(title).then(onPicked)} style={[styles.btn, styles.btnOn]} accessibilityLabel="Start council">
            <Plus size={17} color={t.onGold} />
          </Pressable>
        </View>
        <Text style={[styles.lobbyKicker, { marginTop: 8 }]}>YOUR COUNCILS</Text>
        {c.sessions.length === 0 ? <Text style={styles.empty}>No councils yet.</Text> : null}
        {c.sessions.map((s) => (
          <View key={s.id} style={[styles.sessRow, s.id === c.sessionId && { borderColor: t.goldBright }]}>
            <Pressable
              style={{ flex: 1, gap: 4 }}
              onPress={() => {
                void c.resume(s);
                onPicked();
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Text style={[styles.msgName, { flexShrink: 1 }]} numberOfLines={1}>
                  {s.title}
                </Text>
                <StatusPill status={s.status ?? 'active'} />
              </View>
              <Text style={styles.msgTime} numberOfLines={1}>
                {new Date(s.updated_at).toLocaleDateString()} · {s.members.length ? s.members.map(seatName).join(', ') : 'no seats yet'}
              </Text>
            </Pressable>
            <Pressable onPress={() => void c.remove(s.id)} hitSlop={8} accessibilityLabel={`Delete council session ${s.title}`}>
              <Trash2 size={16} color={t.ivoryFaint} />
            </Pressable>
          </View>
        ))}
      </ScrollView>
    </View>
  );
}

/** Caption of the latest line, @ tags, hands-free mic, text box, send. */
export function CouncilComposer({ c, bottom }: { c: Council; bottom: number }) {
  const t = useV10();
  const styles = useStyles();
  const [draft, setDraft] = React.useState('');
  const last = [...c.lines].reverse().find((l) => l.kind === 'message' && l.speaker !== 'user');
  const caption = c.thinking ? `${seatName(c.thinking)} is thinking…` : last ? `${seatName(last.speaker)}: ${last.content}` : '';
  const ear = c.ear;
  const micLabel = !ear.on ? ear.error ?? 'Mic off' : ear.state === 'connecting' ? 'Connecting mic…' : c.play.id ? 'Mic paused while they speak' : ear.hearing ? 'Hearing you…' : 'Listening';
  const submit = () => {
    void c.send(draft);
    setDraft('');
  };
  return (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={[styles.bottom, { paddingBottom: bottom + 10 }]} pointerEvents="box-none">
      {caption ? (
        <Text style={styles.caption} numberOfLines={2}>
          {caption}
        </Text>
      ) : null}
      <View style={styles.inputRow}>
        <Pressable onPress={() => (ear.on ? ear.stop() : void ear.start())} style={[styles.btn, ear.on && styles.btnOn]} accessibilityLabel={ear.on ? 'Turn the council mic off' : 'Turn the council mic on'}>
          {ear.on ? <Mic size={18} color={t.onGold} /> : <MicOff size={18} color={t.goldBright} />}
        </Pressable>
        <TextInput value={draft} onChangeText={setDraft} onSubmitEditing={submit} returnKeyType="send" style={styles.input} />
        <Pressable onPress={submit} style={[styles.btn, styles.btnOn]} accessibilityLabel="Send to the council">
          <Send size={17} color={t.onGold} />
        </Pressable>
      </View>
      {ear.error && !ear.on ? <Text style={[styles.micLabel, { color: t.lapsed }]}>{micLabel}</Text> : null}
    </KeyboardAvoidingView>
  );
}

/** Tap a seated member: Nudge / Mute / talk one-on-one / change who sits here / remove. */
export function CouncilSeatMenu({ c, id, bottom, onClose, onTalk, onChange }: { c: Council; id: string; bottom: number; onClose: () => void; onTalk: (id: string) => void; onChange?: () => void }) {
  const t = useV10();
  const styles = useStyles();
  const rows: { icon: React.ReactNode; label: string; fn: () => void }[] = [
    { icon: <Hand size={16} color={t.goldBright} />, label: 'Nudge to speak', fn: () => void c.nudge(id) },
    { icon: c.muted.includes(id) ? <Mic size={16} color={t.goldBright} /> : <MicOff size={16} color={t.goldBright} />, label: c.muted.includes(id) ? 'Unmute' : 'Mute', fn: () => c.toggleMute(id) },
    { icon: <Phone size={16} color={t.goldBright} />, label: 'Talk one-on-one', fn: () => onTalk(id) },
  ];
  if (onChange) rows.push({ icon: <Repeat size={16} color={t.goldBright} />, label: 'Change who sits here', fn: onChange });
  rows.push({ icon: <UserMinus size={16} color={t.goldBright} />, label: 'Remove from council', fn: () => c.unseat(id) });
  return (
    <Pressable style={StyleSheet.absoluteFill} onPress={onClose}>
      <View style={[styles.menu, { bottom }]}>
        <Text style={styles.menuTitle}>{seatName(id)}</Text>
        {rows.map((o) => (
          <Pressable
            key={o.label}
            onPress={() => {
              onClose();
              o.fn();
            }}
            style={styles.menuRow}
          >
            {o.icon}
            <Text style={styles.menuText}>{o.label}</Text>
          </Pressable>
        ))}
      </View>
    </Pressable>
  );
}

/** An empty podium was tapped: pick which of that room's deities takes the seat. */
export function CouncilPicker({ title, options, bottom, onPick, onClose }: { title: string; options: { id: string; label: string; sub: string }[]; bottom: number; onPick: (id: string) => void; onClose: () => void }) {
  const t = useV10();
  const styles = useStyles();
  return (
    <Pressable style={StyleSheet.absoluteFill} onPress={onClose}>
      <View style={[styles.menu, { bottom, maxHeight: 420 }]}>
        <Text style={styles.menuTitle}>{title}</Text>
        <ScrollView>
        {options.map((o) => (
          <Pressable
            key={o.id}
            onPress={() => {
              onClose();
              onPick(o.id);
            }}
            style={styles.menuRow}
          >
            {o.id === 'syqi' ? (
              <View style={styles.you}>
                <Butterfly size={16} color={t.goldBright} />
              </View>
            ) : (
              <Medallion id={o.id} size={30} />
            )}
            <View style={{ flex: 1 }}>
              <Text style={styles.menuText}>{o.label}</Text>
              <Text style={styles.msgTime}>{o.sub}</Text>
            </View>
          </Pressable>
        ))}
        </ScrollView>
      </View>
    </Pressable>
  );
}

const STATUS_COLOR: Record<string, string> = { active: '#5ec47a', paused: '#e3b341', closed: '#8a93a6' };
export function StatusPill({ status }: { status: string }) {
  const styles = useStyles();
  return (
    <View style={styles.pill}>
      <View style={[styles.dot, { backgroundColor: STATUS_COLOR[status] ?? STATUS_COLOR.closed }]} />
      <Text style={styles.pillText}>{status === 'active' ? 'ACTIVE' : status.toUpperCase()}</Text>
    </View>
  );
}

/** Room header on the floor: name (tap to rename), status pill, pause / resume. */
export function CouncilHeader({ c, top }: { c: Council; top: number }) {
  const t = useV10();
  const styles = useStyles();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const has = !!c.sessionId;
  return (
    <View style={[styles.header, { top }]} pointerEvents="box-none">
      {editing ? (
        <TextInput
          autoFocus
          value={draft}
          onChangeText={setDraft}
          onBlur={() => {
            setEditing(false);
            void c.rename(draft);
          }}
          onSubmitEditing={() => {
            setEditing(false);
            void c.rename(draft);
          }}
          style={[styles.input, { height: 34, flex: 0, minWidth: 160 }]}
        />
      ) : (
        <Pressable
          onPress={() => {
            if (!has) return;
            setDraft(c.name);
            setEditing(true);
          }}
          accessibilityLabel="Rename this council"
        >
          <Text style={styles.headerName} numberOfLines={1}>
            {has ? c.name || 'Council' : 'New council'}
          </Text>
        </Pressable>
      )}
      {has ? <StatusPill status={c.status} /> : null}
      {has && c.status !== 'closed' ? (
        <Pressable onPress={() => void c.setRoomStatus(c.status === 'paused' ? 'active' : 'paused')} style={styles.headerBtn} accessibilityLabel={c.status === 'paused' ? 'Resume council' : 'Pause council'}>
          {c.status === 'paused' ? <PlayIcon size={12} color={t.goldBright} /> : <PauseIcon size={12} color={t.goldBright} />}
          <Text style={styles.headerBtnText}>{c.status === 'paused' ? 'Resume' : 'Pause'}</Text>
        </Pressable>
      ) : null}
      {has && c.status === 'closed' ? (
        <Pressable onPress={() => void c.setRoomStatus('active')} style={styles.headerBtn} accessibilityLabel="Reopen council">
          <PlayIcon size={12} color={t.goldBright} />
          <Text style={styles.headerBtnText}>Reopen</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

export function CouncilLineCount({ lines }: { lines: CouncilLine[] }) {
  const styles = useStyles();
  return <Text style={styles.count}>{lines.filter((l) => l.kind === 'message').length}</Text>;
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    float: { position: 'absolute', right: 14, width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(9,14,32,0.8)', borderWidth: 1, borderColor: 'rgba(214,180,106,0.6)', zIndex: 6 },
    floatOn: { backgroundColor: 'rgba(214,180,106,0.28)', borderColor: t.goldBright, borderWidth: 2 },
    sheet: { position: 'absolute', borderRadius: 16, backgroundColor: '#1b2a4e', borderWidth: 1, borderColor: 'rgba(168,196,240,0.35)', zIndex: 5 },
    lobbyKicker: { color: 'rgba(190,210,245,0.8)', fontSize: 10, fontWeight: '800', letterSpacing: 2 },
    pill: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999, borderWidth: 1, borderColor: 'rgba(168,196,240,0.35)', backgroundColor: 'rgba(9,14,32,0.5)' },
    dot: { width: 6, height: 6, borderRadius: 3 },
    pillText: { color: t.ivory, fontSize: 9, fontWeight: '800', letterSpacing: 1 },
    header: { position: 'absolute', left: 12, right: 60, flexDirection: 'row', alignItems: 'center', gap: 8, zIndex: 6 },
    headerName: { color: t.ivory, fontSize: 15, fontWeight: '800', letterSpacing: 0.5, textShadowColor: 'rgba(0,0,0,0.9)', textShadowRadius: 6, maxWidth: 180 },
    headerBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 9, paddingVertical: 4, borderRadius: 999, borderWidth: 1, borderColor: 'rgba(214,180,106,0.6)', backgroundColor: 'rgba(9,14,32,0.8)' },
    headerBtnText: { color: t.goldBright, fontSize: 11, fontWeight: '800' },
    empty: { color: t.ivoryFaint, fontStyle: 'italic', fontSize: 13, paddingVertical: 8 },
    sys: { color: t.ivoryFaint, fontSize: 12, fontStyle: 'italic', paddingLeft: 8, borderLeftWidth: 2, borderLeftColor: 'rgba(214,180,106,0.35)' },
    msgHead: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    msgName: { color: t.ivory, fontSize: 13, fontWeight: '800', letterSpacing: 0.6 },
    msgTime: { color: t.ivoryFaint, fontSize: 11 },
    msgText: { color: '#f3ead2', fontSize: 13, lineHeight: 18 },
    quote: { color: t.ivoryFaint, fontSize: 11, fontStyle: 'italic' },
    you: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', borderWidth: 2, borderColor: t.gold, backgroundColor: 'rgba(16,24,48,0.92)' },
    youText: { color: t.goldBright, fontSize: 8, fontWeight: '900' },
    playBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999, borderWidth: 1, borderColor: t.gold },
    playText: { color: t.goldBright, fontSize: 11, fontWeight: '800' },
    sessRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10, borderRadius: 14, borderWidth: 1, borderColor: 'rgba(214,180,106,0.3)' },
    newBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, height: 40, borderRadius: 20, backgroundColor: t.gold },
    newText: { color: t.onGold, fontWeight: '800', fontSize: 13 },
    menu: { position: 'absolute', left: 24, right: 24, padding: 12, gap: 4, borderRadius: 18, backgroundColor: 'rgba(9,14,32,0.97)', borderWidth: 1, borderColor: t.gold, zIndex: 8 },
    menuTitle: { color: t.goldBright, fontSize: 13, fontWeight: '900', letterSpacing: 2, textTransform: 'uppercase', paddingBottom: 4 },
    menuRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10 },
    menuText: { color: t.ivory, fontSize: 15 },
    bottom: { position: 'absolute', left: 0, right: 0, bottom: 0, paddingHorizontal: 12, gap: 6, zIndex: 4 },
    caption: { color: t.ivory, fontSize: 13, lineHeight: 18, textAlign: 'center', paddingHorizontal: 8, textShadowColor: 'rgba(0,0,0,0.9)', textShadowRadius: 6 },
    inputRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    input: { flex: 1, minWidth: 0, height: 44, borderRadius: 22, paddingHorizontal: 16, backgroundColor: '#ffffff', borderWidth: 1.5, borderColor: t.gold, color: '#111827', fontSize: 15 },
    btn: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: t.gold, backgroundColor: 'rgba(9,14,32,0.85)' },
    btnOn: { backgroundColor: t.gold },
    micLabel: { color: t.goldBright, fontSize: 11, fontWeight: '800', letterSpacing: 1, textAlign: 'center', textShadowColor: 'rgba(0,0,0,0.9)', textShadowRadius: 6 },
    count: { color: t.goldBright, fontSize: 10, fontWeight: '900' },
  }),
);
